import { z } from 'zod'
import { GridApiPro } from '@mui/x-data-grid-pro'

declare module '@mui/x-data-grid-pro' {
    interface ToolbarPropsOverrides {
        exportFilename?: string
        exporting?: boolean
        exportAllRows?: <
            T extends (params: {
                filename: string
                includeHeaders: boolean
            }) => void,
        >(
            apiRef: React.MutableRefObject<GridApiPro>,
            exporter?: T,
            filename?: string
        ) => Promise<void>
        exportAs?: 'csv' | 'xlsx' | 'print'
    }
}

type Currency = 'USD' | 'CAD' | 'GBP'

export interface Org {
    id: string
    name: string
    created_date: string
    address: string | null
    state: string | null
    city: string | null
    postcode: string | null
    country: string | null
    active: 0 | 1
    department: string | null
    limit_users: number
    team_id: string | null
    timezone: string | null
    currency: Currency
    purchase_order_reference: string | null
    org_type: string | null
    licence_initial_bill_date: string | null
    approved: boolean
    review_date: Date | null
    denial_reason: string | null
    notes: string | null
    expected_users: number
}

/**
 * Operational status reported by a Podium's device shadow (plus 'offline', which Habitat Admin
 * writes from AWS IoT presence). Capture-only — mirrors Habitat Admin's `Podium['status']`.
 */
export type PodiumStatus =
    | 'initializing'
    | 'standby'
    | 'recording'
    | 'paused'
    | 'offline'
    | 'disabled'

/**
 * A row from Habitat Admin's staff fleet endpoint (`GET /v2/podium/fleet`): the `Podiums` columns
 * plus the joined organisation name. Dates arrive as ISO strings over the wire. Mirrors Habitat
 * Admin's `PodiumFleetRow`.
 */
export interface PodiumFleetRow {
    id: string
    org_id: string | null
    org_name: string | null
    hardware_id: string | null
    name: string | null
    description: string | null
    status: PodiumStatus
    is_online: boolean
    recording_started_at?: string | null
    app_version?: string | null
    disk_free_percent?: number | null
    failed_job_count?: number | null
    uptime_seconds?: number | null
    last_health_at?: string | null
    decommission_requested_at?: string | null
    decommissioned_at?: string | null
    desired_admin_state?: 'active' | 'disabled' | 'wipe' | null
    created_at: string
    updated_at: string
}

/**
 * One persisted device event from Habitat Admin's `PodiumEvents` log (90-day retention):
 * error events the device published, plus drift-reconciliation audit rows. Mirrors Habitat
 * Admin's `PodiumEvent`; dates arrive as ISO strings over the wire.
 */
export interface PodiumEvent {
    id: string
    podium_id: string
    hardware_id: string
    type: 'error' | 'drift-reconciled'
    stage: string | null
    message: string | null
    upload_id: string | null
    session_id: string | null
    created_at: string
}

/**
 * One row of Habitat Admin's `PodiumJobs` table: the current state of a processing job on a
 * device, upserted from its `jobs/state` MQTT events. `status` is the coarse axis — uploading
 * reports as `running` with `stage: 'uploading'`, so "active" is one status, not two.
 *
 * `device_present` false means the device no longer reports this job (dismissed, pruned, or
 * disk-reclaimed). A failed row that is absent has lost its recording file and can never be
 * retried — which is why Retry is disabled on those.
 */
export interface PodiumJobRow {
    id: string
    podium_id: string
    hardware_id: string
    job_id: string
    upload_id: string | null
    session_id: string | null
    status: 'queued' | 'running' | 'complete' | 'failed'
    stage: string | null
    failure_reason: string | null
    /** tinyint over the wire — read for truthiness, not identity. */
    recoverable: boolean | null
    needs_attention: boolean
    retry_count: number
    error_message: string | null
    device_started_at: string | null
    device_updated_at: string
    device_seq: number
    device_present: boolean
    completed_at: string | null
    created_at: string
    updated_at: string
}

/** Device-authored hardware snapshot, captured at onboarding. Every field is best-effort. */
export interface PodiumHardwareInfo {
    modelIdentifier?: string
    marketingName?: string
    chip?: string
    memoryBytes?: number
    serialNumber?: string
    osVersion?: string
    osBuild?: string
}

/** A camera or microphone as the device reports it. Device-authored: assume nothing. */
export interface PodiumPeripheral {
    id?: string
    name?: string
    connection?: string
    maxResolution?: string
    isActive?: boolean
}

/**
 * Readiness of the three on-device AI models, from the last heartbeat. Statuses share
 * one vocabulary: `unknown` | `checking` | `downloading` | `installed` | `failed`.
 *
 * A station missing one of these can record but cannot finish post-processing, so this
 * is the other half of "why is this device not producing notes".
 */
export interface PodiumModelState {
    transcription: string | null
    diarization: string | null
    summarization: string | null
    /** Installed summary-model version, for watching a staged model rollout. */
    summarizationVersion: number | null
    /** False means the device is a model version behind, with the upgrade pending. */
    summarizationUpToDate: boolean | null
    /** Only populated while that model's status is `failed`. */
    transcriptionError: string | null
    diarizationError: string | null
    summarizationError: string | null
}

/**
 * The device's Sparkle auto-update posture, as Habitat Admin lifts it out of the last
 * heartbeat. Answers "which version is waiting and why hasn't it installed".
 *
 * There is no last-check time here: the device reports its update posture, not Sparkle's
 * poll history, so the freshness of all of this is the podium's `last_health_at`.
 */
export interface PodiumUpdateState {
    /** `dormant` | `idle` | `downloading` | `staged` | `installing` | `failed`. */
    status: string | null
    stagedVersion: string | null
    stagedAt: string | null
    /** Why the last install attempt deferred (outside the window, device not idle). */
    blockedReason: string | null
    lastError: string | null
    /** Why this deployment can't self-update at all — needs an attended fix on site. */
    deploymentIssue: string | null
}

/**
 * `GET /v2/podium/:hardwareId` — the single-device payload behind the detail page. Mirrors
 * Habitat Admin's `StaffPodiumDetail` (an allow-list: the claim secrets and IoT identity
 * never leave the server). Dates arrive as ISO strings.
 */
export interface PodiumDetail {
    id: string
    hardware_id: string | null
    name: string | null
    description: string | null
    org_id: string | null
    org_name: string | null

    status: PodiumStatus
    is_online: boolean
    recording_started_at: string | null
    status_updated_at: string | null
    desired_admin_state: 'active' | 'disabled' | 'wipe' | null
    needs_rotating: boolean

    created_at: string
    updated_at: string
    decommission_requested_at: string | null
    decommissioned_at: string | null

    app_version: string | null
    app_build: string | null
    launch_at_login: boolean | null
    update: PodiumUpdateState | null
    models: PodiumModelState | null
    disk_free_percent: number | null
    failed_job_count: number | null
    uptime_seconds: number | null
    last_health_at: string | null
    camera_permission: string | null
    microphone_permission: string | null
    last_error: string | null
    last_error_at: string | null

    hardware: PodiumHardwareInfo | null
    cameras: PodiumPeripheral[]
    microphones: PodiumPeripheral[]
}

/** The knex-paginate length-aware pagination block returned by the fleet endpoint. */
export interface PodiumFleetPagination {
    total: number
    lastPage: number
    prevPage: number | null
    nextPage: number | null
    perPage: number
    currentPage: number
    from: number
    to: number
}

export interface PodiumFleetResult {
    data: PodiumFleetRow[]
    pagination: PodiumFleetPagination
}

/** `GET /v2/podium/:hardwareId/events` — an event page plus the device identity echoed back. */
export interface PodiumEventsResult {
    podiumId: string
    hardwareId: string | null
    name: string | null
    data: PodiumEvent[]
    pagination: PodiumFleetPagination
}

/**
 * One Messenger Pigeon session's name, from `GET /api/overviews/session-titles`. Podium
 * job and event rows carry MP's session id but nothing readable, so the console resolves
 * the titles itself — live rather than copied, because titles stay editable in MP.
 */
export interface SessionTitle {
    title: string
    /** Non-null for a soft-deleted session: shown, and marked, rather than hidden. */
    deletedAt: string | null
}

/** `GET /v2/podium/:hardwareId/jobs` — a job page plus the device identity echoed back. */
export interface PodiumJobsResult {
    podiumId: string
    hardwareId: string | null
    name: string | null
    data: PodiumJobRow[]
    pagination: PodiumFleetPagination
}

/**
 * Columns the fleet endpoint will sort by. Must match Habitat Admin's `PODIUM_FLEET_SORT_COLUMNS`
 * — the frontend only forwards `orderBy` when the grid's sort field is one of these (the server
 * rejects anything else), so any other column stays client-display-only.
 */
export const PODIUM_FLEET_SORT_COLUMNS = [
    'name',
    'status',
    'is_online',
    'failed_job_count',
    'disk_free_percent',
    'uptime_seconds',
    'app_version',
    'last_health_at',
    'created_at',
    'updated_at',
] as const
export type PodiumFleetSortColumn = (typeof PODIUM_FLEET_SORT_COLUMNS)[number]

export type MPSession = {
    id: string
    userId: string
    title: string
    createdAt: string
    updatedAt: string
    deletedAt: string | null
    originalCreatedAt: string
    nteUserProjectId: number | null
}

export type MPOrder = {
    id: string
    userId: string
    sessionId: string | null
    uploadId: string | null
    orderType: string
    orderStatus: string
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    orderOptions: { [key: string]: any } | null
    totalAmount: number | null
    unitAmount: number | null
    currency: string | null
    stripePaymentIntentId: string | null
    stripePaymentIntentStatus: string | null
    createdAt: string
    updatedAt: string
    beehiveNoteTakingTaskId: string | null
    stripeAccount: string | null
    paidBy: string
}

export type MPUploadType =
    | 'mp-instant-session'
    | 'mp-mobile-recording'
    | 'mp-document'
    | 'mp-desktop-recording'
    | 'mp-video'

export type MPUpload = {
    id: string
    userId: string
    sessionId: MPSession['id']
    type: MPUploadType
    mimeType: string
    status: string
    duration: number | null
    s3Key: string | null
    s3Bucket: string | null
    multipartUploadId: string | null
    multipartUploadCreatedAt: Date | null
    multipartUploadCompletedAt: Date | null
    createdAt: string
    updatedAt: string
    deletedAt: string | null
    anthillProjectId: string | null
    anthillProjectStatus: string | null
    originalCreatedAt: string
    audioS3Key: string | null
    title: string | null
    fileSize: number | null
}

export type Order = {
    id: string
    title: string | null
    user_id: string
    organization_id: string | null
    service_type: string
    source: string | null
    source_id: string | null
    duration: number | null
    turnaround_time: 24 | 48
    rate: number | null
    cost: number
    cost_currency: string
    origin: string | null
    created_date: string | null
}

export interface ServicePrices {
    id: string
    name: 'summary_notes'
    turnaround_time: number
    unit_cost: number
    currency: string
}

export interface UsersServicesAllowed {
    id: string
    service_id: string
    user_id: string
    from_date: Date
    to_date?: Date
}

export interface UsersServicesBlocked {
    id: string
    service_id: string
    user_id: string
    from_date: Date
    to_date?: Date
}

export interface OrganizationServices {
    id: string
    service_id: string
    org_id: string
    from_date: string
    to_date?: string
}

export type LimitPeriod = 'full-month' | 'half-month'
export interface UserLimit {
    id: string
    user_id: string
    service_id: ServicePrices['id']
    limit: number
    period: LimitPeriod
    from_date: Date
    to_date?: Date
}

export interface OrgDefaultLimit {
    id: string
    org_id: string
    service_id: ServicePrices['id']
    limit: number
    period: LimitPeriod
    from_date: Date
    to_date?: Date
}

/** endpoint returns */

enum Role {
    user = 'user',
    admin = 'admin',
    superadmin = 'superadmin',
    atsp = 'atsp',
    atsp_user = 'atsp-user',
    superAtsp = 'superAtsp',
    dsa = 'dsa',
    dsa_user = 'dsa-user',
}

type RoleName = `${Role}`
export interface HabitatAdminUser {
    id: string
    beehive_id: string
    sms_id: string | null
    nte_id: string | null
    org_id: string | null
    aorg_id: string | null
    uorg_id: string | null
    dorg_id: string | null
    team_id: string | null
    email: string
    first_name: string | null
    last_name: string | null
    active: boolean
    limit_users: number | null
    role: RoleName | null
    reference_id: string | null
    created_date: string | null
    comment: string | null
    phone: string | null
    api_token: string | null
    last_login: string | null
    timezone: string | null
    token_valid: string | null
    deleted_date: string | null
    group?: {
        id: string
        name: string
    }
}

export type GetOrgUsersReturnType = (Pick<
    HabitatAdminUser,
    'id' | 'email' | 'first_name' | 'last_name'
> & {
    role?: RoleName | null
})[]

export type OrgOverview = Org & {
    groups: GroupWithCount[]
    review_status: 'pending' | 'approved' | 'denied'
}

export interface LicenceAssignment {
    id: string
    licence_id: string
    user_id: string
    from_date: string | null
    to_date: string | null
    created_at: string
}

export interface LicenceTier {
    id: string
    name: string
    created_at: string
}

export interface LicenceOrg {
    licence_initial_bill_date: string | null
    currency: string | null
    country: string | null
}

export interface Licence {
    id: string
    org_id: string
    tier_id: string
    trial: number
    billing_frequency: string | null
    from_date: string | null
    to_date: string | null
    purchased_at: string | null
    created_at: string
    assignments: LicenceAssignment[]
    tier: LicenceTier | null
    features: unknown[]
    org: LicenceOrg | null
    status: string
    activeAssignment: LicenceAssignment | null
}

export type ErrorResponse = {
    error: true
    message: string
    code: number
    caughtError?: Error
    name?: string
}

export type ResponseFormat<t = object> = ErrorResponse | ({ error?: false } & t)

export interface Group {
    id: string
    name: string
    description: string
    org_id: string
    created_at: string
    updated_at: string
    deleted_at?: string
}

export interface GroupUser {
    id: string
    user_id: string
    group_id: Group['id']
    joined_date: Date
    left_date?: Date
}

export interface GroupServiceAllowed {
    id: string
    group_id: Group['id']
    service_id: ServicePrices['id']

    from_date: Date
    to_date?: Date
    created_at: Date
}

export interface GroupServiceBlocked {
    id: string
    group_id: Group['id']
    service_id: ServicePrices['id']
    from_date: Date
    to_date?: Date
    created_at: Date
}

export interface GroupLimit {
    id: string
    group_id: Group['id']
    service_id: ServicePrices['id']
    limit: number
    period: 'full-month' | 'half-month'
    from_date: string
    to_date?: string
    created_at: Date
}

export type GroupServiceSetting =
    | {
          type: 'allowed'
          setting: GroupServiceAllowed
      }
    | {
          type: 'blocked'
          setting: GroupServiceBlocked
      }

export type GroupSummary = Group & {
    users: HabitatAdminUser[]
    serviceSetting?: GroupServiceSetting
    service?: ServicePrices
    limit?: GroupLimit
}

export interface GroupWithCount {
    id: Group['id']
    name: Group['name']
    userCount: number
}

interface OrgCollection {
    id: string
    name: string
    created_date: Date | null
}

interface OrgUser {
    id: string
    org_id: string
    role: string
    user_id: string
    email: string
    org_collection_id: string
    created_date: Date | null
}

interface OrgCollectionsUsers {
    id: string
    org_collection_id: string
    role: string
    user_id: string
    email: string
    created_date: Date
}

export interface OrgCollectionFull extends OrgCollection {
    organizations: Org[]
    orgUsers: OrgUser[]
    orgCollectionsUsers: OrgCollectionsUsers[]
}

export type NotesLimitLevel = 'user' | 'group' | 'org' | 'fallback'

/**
 * Why habitat-admin answered the way it did.
 *
 * The distinction that matters here is 'blocked-by-rule' against
 * 'limit-reached'. Both mean the student cannot order right now, but only the
 * first means anything is wrong with their rule.
 */
export type NotesOrderReason =
    | 'allowed'
    | 'org-not-approved'
    | 'blocked-by-rule'
    | 'limit-reached'
    | 'no-rule'

export type HAUserSettings = {
    usage: number
    canOrder: boolean
    /** Which of the two independent checks decided. */
    reason: NotesOrderReason
    service: ServicePrices | null
    group: Group | null
    /**
     * Which level supplied the limit. Resolved separately from the rule, so it
     * is frequently a different level from settingLevel. 'fallback' means no
     * level had one and habitat-admin's house default applied.
     */
    limitLevel: NotesLimitLevel | null
    /** Seconds allowed in the period, set even when limit itself is null. */
    limitSeconds: number | null
} & (
    | {
          settingLevel: 'user'
          setting: UsersServicesAllowed | UsersServicesBlocked | null
          limit: UserLimit | null
      }
    | {
          settingLevel: 'group'
          setting: GroupServiceAllowed | GroupServiceBlocked | null
          limit: GroupLimit | null
      }
    | {
          settingLevel: 'org'
          setting: OrganizationServices | null
          limit: OrgDefaultLimit | null
      }
)

export type HAUserSettingsRes =
    | {
          error: false
          result: HAUserSettings
      }
    | {
          error: true
          result: string
      }

export type HAOk<T> = {
    error: false
    result: T
}

export type HAErr = {
    error: true
    result: string
}

export type HAResult<T> = HAOk<T> | HAErr

// Shape of one row in habitat-admin's `SsoConnections` table, surfaced
// through `/v2/sso-connections/*`. Mirrors the server-side type in
// habitat-admin-server/src/@types/index.d.ts. JSON-typed columns
// (`attribute_map`, `email_domains`) come back rehydrated from the
// model's `deserialize` helper, so the wire shape is the parsed form.
export type SsoProviderType = 'saml'

export interface SsoConnection {
    id: string
    org_id: string
    connection_id: string
    provider_type: SsoProviderType
    idp_entity_id: string
    idp_sso_url: string
    idp_metadata_url: string | null
    idp_metadata_xml: string | null
    idp_signing_cert: string | null
    nameid_format: string
    attribute_map: Record<string, string>
    // Optional UPN-attribute name. NULL = first-link gate keys on
    // the `email` attribute's domain (pre-2026-05-03 default,
    // correct for most IdPs). Set to the IdP's UPN-carrying
    // attribute name for AD/Azure AD-style federations where the
    // `email` attribute carries a different domain than the user
    // logs in with. See `docs/onboard-tenant.md` §1.4 for the
    // operator-facing rationale.
    username_attribute: string | null
    allow_idp_initiated: 0 | 1
    enforce_sso: 0 | 1
    enabled: 0 | 1
    require_response_signature: 0 | 1
    email_domains: string[]
    // Bookkeeping for the metadata-refresh cron (see
    // habitat-admin's saml-metadata-ingester service). NULL on rows
    // that have never been refreshed (or rows in `triple` mode —
    // nothing to ingest). `last_metadata_error` clears on the next
    // successful refresh.
    last_metadata_refresh: string | null
    last_metadata_error: string | null
    created_at: string
    updated_at: string
}

// Habitat-admin sso-connections endpoints all wrap the payload as
// `{ error: false, result }` on success or `{ error: true, message }`
// on failure. Distinct from the `HAOk<T>` / `HAErr` shape above
// (which uses `data` / `result` for org-collections).
export type HASsoOk<T> = { error: false; result: T }
export type HASsoErr = { error: true; message: string }
export type HASsoResult<T> = HASsoOk<T> | HASsoErr

export const zReviewBody = z
    .object({
        reviewed_by: z.string().email(),
        notes: z.string().optional(),
    })
    .and(
        z
            .object({
                status: z.literal('approved'),
            })
            .or(
                z.object({
                    status: z.literal('denied'),
                    denial_reason: z.string().nonempty(),
                })
            )
    )

export type ReviewBody = z.infer<typeof zReviewBody>

// {
//     canOrder: boolean;
//     settingLevel: "user" | "org" | "group";
//     setting: UsersServicesAllowed | UsersServicesBlocked | OrganizationServices | GroupServiceAllowed | GroupServiceBlocked | null;
//     service: ServicePrices | null;
//     group: Group | null;
//     limit: UserLimit | OrgDefaultLimit | GroupLimit | null;
// }
export type CreateLicenseBody = {
    licenses: {
        licenseTier: string
        term: 'monthly' | 'yearly'
        quantity: number
    }[]
    org_id: string
}

export type AllUserListRes = {
    id: string
    first_name?: string
    last_name?: string
    email: string
}

export type UserSearchResponse = {
    id: string
    name: string
    first_name: string | null
    last_name: string | null
    email: string
    created_at: Date
    deleted_at: Date
}

export type UpdateRoleResponse = {
    habitatAdmin: null | true
}

export type UpdateEmailResponse = {
    error: boolean
    result: string
    errors: {
        beehive: null | true
        accounts: null | true
        habitatAdmin: null | true
    }
}

export type UpdateUserRes = {
    message: string
    /**
     * Each part of the edit reports separately, because they can fail
     * independently. `org` and `role` are one write each; an email
     * change touches three systems and says which of them did not take.
     */
    errors: {
        org: boolean
        role: boolean
        email: UpdateEmailResponse['errors']
    }
}

export type DeleteUserRes = {
    message: string
    errors: Record<string, unknown>
}

export type SendOrderInvoicesRes = {
    created: {
        allNames: string[]
    }
    failed: {
        allFailed: string[]
    }
}

export type SendLicenseInvoicesRes = {
    created: {
        allNames: string[]
        allIds: string[]
    }
    failed: {
        byId: Record<string, string>
        allFailed: string[]
    }
}

/**
 * A user's free recording allowance in Messenger Pigeon, as returned by
 * `GET /users/:userId/free-hours`.
 *
 * Durations are milliseconds. The fields are separate rather than reduced to
 * one number because no single number is honest: a subscriber and an
 * instructor have no cap at all, and a personal account opened on or after the
 * licensing change has a cap it can never refresh.
 */
export interface FreeHoursSnapshot {
    /** The allowance in whole hours: 5, or 30 for anyone predating the change. */
    limitHours: number
    limitMs: number
    /** Recorded since the last refresh. */
    recordedMs: number
    remainingMs: number
    /** Notes ordered since the last refresh; this is what earns the refresh. */
    orderedMs: number
    /** Still to order before the allowance comes back. */
    orderedNeededMs: number
    /** How much ordering earns a refresh. Currently three hours. */
    orderThresholdMs: number
    lastRefreshedAt: string
    /**
     * Whether that date is stored against the user or inferred. Messenger
     * Pigeon stamps it lazily, so an untouched account has none and it is
     * worked back from their most recent order.
     */
    lastRefreshStamped: boolean
    subscribed: boolean
    instructor: boolean
    /** Whether any cap applies. False for subscribers and instructors. */
    capped: boolean
    /** Whether ordering notes can ever bring the allowance back. */
    canRefresh: boolean
    exhausted: boolean
    /**
     * They have ordered enough and the allowance returns when they next open
     * Messenger Pigeon. The refresh happens on their visit, not at order time.
     */
    refreshPending: boolean
    orgAge: string | null
    accountCreatedAt: string | null
    limitCutOffDate: string
}

/**
 * The Xero tenants we hold contacts in. Orgs outside these (e.g. UK) have no
 * region and so can't be linked to a contact.
 */
export type XeroRegion = 'USA' | 'CAN'

export type XeroContactOption = {
    id: string
    name: string
    region: XeroRegion
    url: string
}

/**
 * One org's current and suggested (exact name match) Xero contact, each with a
 * ready-built deep-link into Xero. Names aren't sent - they're resolved from the
 * contact list that comes back alongside these rows.
 */
export type OrgXeroContactRow = {
    orgId: string
    orgName: string
    region: XeroRegion | null
    currentXeroContactId: string | null
    currentXeroContactUrl: string | null
    suggestedXeroContactId: string | null
    suggestedXeroContactUrl: string | null
}

export type OrgXeroContactsRes = {
    contacts: XeroContactOption[]
    orgs: OrgXeroContactRow[]
}

export type AssignOrgXeroContactsRes = {
    applied: string[]
    failed: { orgId: string; reason: string }[]
}
